import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'Our Design', path: '/our-design' },
  { label: 'Services', path: '/services' },
  { label: 'How We Work', path: '/how-we-work' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none">
          <span
            className={`font-display text-2xl font-semibold tracking-wide transition-colors duration-300 ${
              scrolled || !isHome ? 'text-charcoal' : 'text-white'
            }`}
          >
            RAGVI
          </span>
          <span
            className={`font-body text-xs tracking-[0.3em] uppercase transition-colors duration-300 ${
              scrolled || !isHome ? 'text-primary-600' : 'text-primary-300'
            }`}
          >
            Design Studio
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map(link => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === '/'}
              className={({ isActive }) =>
                `font-body text-sm tracking-widest uppercase transition-colors duration-300 relative group ${
                  isActive
                    ? scrolled || !isHome ? 'text-primary-600' : 'text-primary-300'
                    : scrolled || !isHome ? 'text-charcoal hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-primary-500 transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* CTA */}
        <Link
          to="/contact"
          className={`hidden lg:inline-flex items-center px-6 py-2.5 text-xs tracking-widest uppercase font-body transition-all duration-300 ${
            scrolled || !isHome
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-white/20 text-white border border-white/40 hover:bg-white hover:text-charcoal'
          }`}
        >
          Get Quote
        </Link>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden flex flex-col gap-1.5 w-8 h-6 justify-center"
          aria-label="Toggle menu"
        >
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className={`block h-px transition-all duration-300 ${
                scrolled || !isHome ? 'bg-charcoal' : 'bg-white'
              } ${
                menuOpen && i === 0 ? 'rotate-45 translate-y-2' :
                menuOpen && i === 1 ? 'opacity-0' :
                menuOpen && i === 2 ? '-rotate-45 -translate-y-2' : ''
              }`}
            />
          ))}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <nav className="flex flex-col px-6 py-4 gap-4">
              {navLinks.map(link => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === '/'}
                  className={({ isActive }) =>
                    `font-body text-sm tracking-widest uppercase py-2 border-b border-gray-100 ${
                      isActive ? 'text-primary-600' : 'text-charcoal'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-2 btn-primary text-center justify-center"
              >
                Get Free Quote
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
